import React, { FC, memo, useMemo, useRef, useState } from 'react'

import { useClickAway } from 'react-use'
import styled from 'styled-components'

import {
  Search,
  ProjectSearchNarrowDown,
  ProjectSearchReset,
  ProjectSearchDuration,
  ProjectSearchStatus,
  ProjectSearchArea,
} from 'src/components/molecules'

import {
  useGA,
  useGetProjectStatuses,
  useProjectStatus,
  useSearchContent,
  useShowRangeCalendar,
} from 'src/hooks'
import { ProjectSearchContentEntity } from 'src/types'

const Wrapper = styled.div`
  border-radius: 8px;
  width: 22.57rem;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  top: -0.57rem;
  left: -0.57rem;
  padding: 5.5rem 0.57rem 0.57rem;
  z-index: 10;
`
const SearchArea = styled.div`
  max-height: 36.14rem;
  overflow-y: auto;
`

type Props = {
  searchContent: ProjectSearchContentEntity | null
  updateSearchContent: (searchContent: ProjectSearchContentEntity | null) => void
  className?: string
}

export const ProjectSearch: FC<Props> = memo(
  ({ searchContent, updateSearchContent, className }) => {
    const [isOpen, setIsOpen] = useState(false)
    const { showRangeCalendar } = useShowRangeCalendar()
    const { loadEnabledSearchContent } = useSearchContent()
    const { getHasArchived } = useProjectStatus()
    const { projectStatuses } = useGetProjectStatuses()
    const { sendEvent } = useGA()

    const ref = useRef(null)
    useClickAway(ref, () => {
      if (!showRangeCalendar && isOpen) {
        setIsOpen(false)
        sendEvent('search_project')
      }
    })

    const handleChangeSearchWord = (word: string) => {
      if (!searchContent) {
        const newSearchContent: ProjectSearchContentEntity = { word }
        updateSearchContent(newSearchContent)
        return
      }

      const updatedSearchContent: ProjectSearchContentEntity = { ...searchContent, word }
      updateSearchContent(updatedSearchContent)
    }

    const handleUpdateDuration = (duration: { startDate: Date; endDate?: Date }) => {
      if (!searchContent) {
        const newSearchContent: ProjectSearchContentEntity = {
          startDate: duration.startDate,
          endDate: duration.endDate,
        }
        updateSearchContent(newSearchContent)
        return
      }
      const updatedSearchContent: ProjectSearchContentEntity = {
        ...searchContent,
        startDate: duration.startDate,
        endDate: duration.endDate,
      }
      updateSearchContent(updatedSearchContent)
    }

    const handleUpdateStatuses = (updatedStatuses: string[]) => {
      if (!searchContent) {
        const newSearchContent: ProjectSearchContentEntity = { selectedStatuses: updatedStatuses }
        updateSearchContent(newSearchContent)
        return
      }

      const updatedSearchContent: ProjectSearchContentEntity = {
        ...searchContent,
        selectedStatuses: updatedStatuses,
      }
      updateSearchContent(updatedSearchContent)
    }

    const handleUpdatePrefectures = (updatedPrefectures: string[]) => {
      if (!searchContent) {
        const newSearchContent: ProjectSearchContentEntity = {
          selectedPrefectures: updatedPrefectures,
        }
        updateSearchContent(newSearchContent)
        return
      }

      const updatedSearchContent: ProjectSearchContentEntity = {
        ...searchContent,
        selectedPrefectures: updatedPrefectures,
      }
      updateSearchContent(updatedSearchContent)
    }

    const disabledSearchContent: boolean = useMemo(() => {
      if (!searchContent) {
        return true
      }

      return !loadEnabledSearchContent(searchContent)
    }, [loadEnabledSearchContent, searchContent])

    return (
      <div className={className}>
        <div className="tw-relative" ref={ref}>
          <Search
            value={searchContent?.word ?? ''}
            onChange={handleChangeSearchWord}
            onFocus={() => setIsOpen(true)}
            placeholder="プロジェクト名称で検索（複数可）"
            className="tw-relative tw-z-20 tw-w-[21.43rem]"
          />
          {isOpen && (
            <Wrapper className="tw-absolute tw-bg-surface-primary tw-border-border-secondary">
              <div className="tw-full-justify">
                <ProjectSearchNarrowDown
                  isSearchEnabled={!!searchContent && loadEnabledSearchContent(searchContent)}
                />
                <ProjectSearchReset
                  disabled={disabledSearchContent}
                  onClick={() => updateSearchContent(null)}
                />
              </div>

              <SearchArea>
                <ProjectSearchDuration
                  startDate={searchContent?.startDate}
                  endDate={searchContent?.endDate}
                  onClickDay={handleUpdateDuration}
                  className="tw-mt-s0"
                />
                <ProjectSearchStatus
                  selectedStatusNames={searchContent?.selectedStatuses ?? []}
                  updateStatusNames={handleUpdateStatuses}
                  hasArchived={getHasArchived(projectStatuses)}
                  className="tw-mt-s2"
                />
                <ProjectSearchArea
                  selectedPrefectures={searchContent?.selectedPrefectures ?? []}
                  updatePrefectures={handleUpdatePrefectures}
                  className="tw-mt-s2"
                />
              </SearchArea>
            </Wrapper>
          )}
        </div>
      </div>
    )
  }
)
